/**
 * Extension Day Card Component
 * Displays single day of an optional extension (pre-tour or post-tour)
 * Similar to DayCard but includes activity level and extension style
 */

import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import ColletteReadMoreToggle from "@/components/common-controls/collette-read-more-toggle";
import Image from "next/image";
import { WistiaPlayer } from "@wistia/wistia-player-react";
import { stripTags } from "@/utils/content-utils";
import type { ExtensionDayCardProps } from "../../types";

const ExtensionDayCard: React.FC<ExtensionDayCardProps> = ({ day, dayNumber, testIdPrefix }) => {
  const t = useTranslations("PDP");
  return (
    <Box
      data-testid={`${testIdPrefix}_edc_${dayNumber}`}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", legacyDesktop: "row" },
        gap: "2.4rem",
        padding: { xs: "1.6rem", md: "2.4rem" }
        // WHY: Stack vertically on tablet/mobile (<992px), horizontal on desktop
        // WHY: Responsive padding matches other PDP components
      }}
    >
      {/* Day Image */}
      <Box
        sx={{
          width: { legacyDesktop: "45.6rem" },
          alignSelf: { xs: "stretch", legacyDesktop: "auto" },
          height: "30.7rem",
          borderRadius: "2rem",
          overflow: "hidden",
          flexShrink: 0,
          position: "relative"
          // WHY: Consistent 30.7rem height across all breakpoints
          // WHY: Stretch to full width on mobile/tablet (<992px), fixed 45.6rem on desktop
        }}
      >
        {day.dayVideo ? (
          <WistiaPlayer
            id={`tour-media-gallery-video-_extension-day-card_${dayNumber}`}
            key={`${dayNumber}-${day.dayVideo}`}
            data-testid={`${testIdPrefix}_edc_${dayNumber}_video`}
            mediaId={day.dayVideo}
            playBarControl={false}
            volumeControl={false}
            settingsControl={false}
            playerColor="var(--theme-palette-common-white)"
            roundedPlayer={20}
            fullscreenControl
            playPauseControl
            playPauseNotifier={false}
            controlsVisibleOnLoad={false}
            bigPlayButton
            seo
          />
        ) : (
          <Image
            src={day.imageUrl}
            alt={day.imageAltTag}
            fill
            sizes="(max-width: 992px) 100vw, 456px"
            style={{ objectFit: "cover" }}
            data-testid={`${testIdPrefix}_edc_${dayNumber}_image`}
          />
        )}
      </Box>

      {/* Day Content */}
      <Stack gap="1.2rem" flex={1}>
        {/* Title: Day X: City */}
        <Typography
          component="h5"
          sx={{
            fontSize: { xs: "1.8rem", md: "2rem" },
            fontWeight: 600,
            lineHeight: 1.3,
            color: "text.primary",
            margin: 0
            // WHY: Matches Figma H5 specs - responsive sizing, Day# is 600, city is 500
          }}
          data-testid={`${testIdPrefix}_edc-day_${dayNumber}`}
        >
          <Box
            component="span"
            sx={{ fontWeight: 600 }}
            data-testid={`${testIdPrefix}_edc-day-label_${dayNumber}`}
          >
            {t("Day")} {dayNumber}:
          </Box>{" "}
          <Box
            component="span"
            sx={{ fontWeight: 500 }}
            data-testid={`${testIdPrefix}_edc-day-heading_${dayNumber}`}
          >
            {day.dayHeading}
          </Box>
        </Typography>

        {/* Details: Accommodation, Activity Level, Meals, Extension Style */}
        <Stack gap="0.8rem">
          {day.accommodation && (
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: 500,
                lineHeight: 1.5
                // WHY: Matches Figma subtitle 2
              }}
              data-testid={`${testIdPrefix}_edc_${dayNumber}-accommodation`}
            >
              <Box
                component="span"
                sx={{ fontWeight: 700 }}
                data-testid={`${testIdPrefix}_edc_${dayNumber}-accommodation-label`}
              >
                {t("Accommodation")}:{" "}
              </Box>
              {day.accommodation}
            </Typography>
          )}

          {day.meals && (
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: 500,
                lineHeight: 1.5
              }}
              data-testid={`${testIdPrefix}_edc_${dayNumber}-meals`}
            >
              <Box
                component="span"
                sx={{ fontWeight: 700 }}
                data-testid={`${testIdPrefix}_edc_${dayNumber}-meals-label`}
              >
                {/* count: 2 to get plural of Meal */}
                {t("Meals", { count: 2 })}:{" "}
              </Box>
              {day.meals}
            </Typography>
          )}
        </Stack>

        {/* Description with Read More */}
        <ColletteReadMoreToggle
          description={stripTags(day.description)}
          linesShown={4}
          testId={`${testIdPrefix}_edc_${dayNumber}`}
          textSx={{
            fontSize: "1.6rem",
            lineHeight: 1.6,
            fontWeight: 400,
            color: "text.primary"
          }}
        />
      </Stack>
    </Box>
  );
};

export default ExtensionDayCard;
